import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Chip, CircularProgress, Alert, Button, Stack } from '@mui/material';
import articleApi from '../services/articleApi';

const ArticleDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [prevNext, setPrevNext] = useState({ prev: null, next: null });

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        setLoading(true);
        const data = await articleApi.getArticle(slug, true);
        // 後端回傳 { article, prev, next }
        if (data.article) {
          setArticle(data.article);
          setPrevNext({ prev: data.prev, next: data.next });
        } else {
          setArticle(data);
          setPrevNext({ prev: null, next: null });
        }
      } catch (e) {
        setError('載入文章失敗');
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center"><CircularProgress /></Box>
      </Container>
    );
  }

  if (error || !article) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error || '文章不存在'}</Alert>
        <Button component={Link} to="/" sx={{ mt: 2 }} variant="outlined">返回首頁</Button>
      </Container>
    );
  }

  const publishedAt = new Date(article.created_at).toLocaleString('zh-TW');

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button component={Link} to="/" variant="text" sx={{ mb: 2 }}>← 返回首頁</Button>
      {/*
      {article.image && (
        <Box sx={{ mb: 3 }}>
          <img
            src={article.image}
            alt={article.title}
            style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '8px' }}
          />
        </Box>
      )}
      */}
      <Typography variant="h4" component="h1" gutterBottom>{article.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        作者：{article.author?.username} ｜ 發布時間：{publishedAt}
      </Typography>
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {(article.tag_list || []).map((t) => (
          <Chip key={t} label={t} size="small" />
        ))}
      </Box>

      <Box sx={{ mb: 3 }}>
        <div 
          dangerouslySetInnerHTML={{ __html: convertHtmlToDisplay(article.content) }}
          style={{
            lineHeight: '1.6',
            fontSize: '16px'
          }}
        />
      </Box>

      <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
        <span>
          {prevNext.prev && (
            <Button variant="outlined" onClick={() => navigate(`/articles/${prevNext.prev.slug}`)}>
              ← 上一篇：{prevNext.prev.title}
            </Button>
          )}
        </span>
        <span>
          {prevNext.next && (
            <Button variant="outlined" onClick={() => navigate(`/articles/${prevNext.next.slug}`)}>
              下一篇：{prevNext.next.title} →
            </Button>
          )}
        </span>
      </Stack>
    </Container>
  );
};

{/* 將 HTML 轉換為可視化內容，保持原始尺寸 */} 
const convertHtmlToDisplay = (html) => {
  if (!html) return '';

  let display = html;
  
  // 處理圖片標籤，添加響應式樣式
  display = display.replace(
    /<img([^>]*?)>/g, 
    (match, attributes) => {
      // 檢查是否有 max-width 屬性
      const maxWidthMatch = attributes.match(/max-width:\s*(\d+)px/);
      // 如果有 max-width 且大於螢幕寬度，則使用螢幕寬度
      if (maxWidthMatch && parseInt(maxWidthMatch[1]) > window.innerWidth*0.8) {
        attributes = attributes.replace(
          /max-width:\s*(\d+)px/,
          `max-width: 100%`
        );
      }
      return `<img${attributes} />`;
    }
  );
  
  // 處理其他基本 HTML 標籤
  display = display
    .replace(/<b>/g, '<strong>')
    .replace(/<\/b>/g, '</strong>')
    .replace(/<i>/g, '<em>')
    .replace(/<\/i>/g, '</em>')
    .replace(/<u>/g, '<span style="text-decoration: underline;">')
    .replace(/<\/u>/g, '</span>')
    .replace(/<br\s*\/?>/g, '<br />')
    .replace(/\n/g, '<br />');
  
  return display;
};

export default ArticleDetail;
